import { useEffect, useMemo, useState } from 'react';
import { api, type ProfitMinusRow } from '../lib/api';
import { ErrorBox, money } from '../components/DataTable';
import { useProfitMonths } from './profitMonths';
import { CalcNote } from '../components/CalcNote';

/**
 * 경영 분석 › 차인 마이너스 — 수익TOOL 「👨‍✈️차인지급액마이너스리스트」 대체(10-24). 서버 `listProfitMinus`.
 * 그 달 기사 정산현황(차주 · 직영기사) 중 차인지급액 0원 미만 · 회사 차주(동영…) 제외.
 */

const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));

export default function ProfitMinus() {
  const [error, setError] = useState<string | null>(null);
  const [months, mon, setMon] = useProfitMonths(setError);
  const [axis, setAxis] = useState<'all' | 'owner' | 'direct'>('all');
  const [rows, setRows] = useState<ProfitMinusRow[] | null>(null);

  useEffect(() => {
    if (!mon) return;
    let cancelled = false;
    setRows(null); setError(null);
    api.listProfitMinus({ mon }).then((r) => { if (!cancelled) setRows(r.rows); }).catch((e) => { if (!cancelled) setError(errMsg(e)); });
    return () => { cancelled = true; };
  }, [mon]);

  const shown = useMemo(() => (rows ?? []).filter((r) => axis === 'all' || r.axis === axis), [rows, axis]);
  const sum = shown.reduce((a, r) => a + r.grand_total, 0);
  const cnt = (k: 'owner' | 'direct') => (rows ?? []).filter((r) => r.axis === k).length;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <select className="field w-auto" value={mon} onChange={(e) => setMon(e.target.value)} aria-label="정산월">
          {months.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <div className="flex gap-1 text-sm">
          {([['all', `전체 ${rows?.length ?? 0}`], ['owner', `차주 ${cnt('owner')}`], ['direct', `직영기사 ${cnt('direct')}`]] as const).map(([k, l]) => (
            <button key={k} className={axis === k ? 'btn-primary' : 'btn'} onClick={() => setAxis(k)}>{l}</button>
          ))}
        </div>
        <CalcNote>기사 정산현황(차주 · 직영기사) 그 달 값 그대로입니다 — 회사 차주(동영(파주) 등)는 뺐습니다. 정산 체크와 무관하게 전부 보입니다.</CalcNote>
        <span className="ml-auto text-xs text-ink-soft">차인지급액 마이너스 합 {money(sum)}</span>
      </div>
      {error && <ErrorBox message={error} />}
      {!rows && !error && <div className="py-8 text-center text-sm text-ink-soft">세는 중…</div>}
      {rows && (
        <div className="card max-h-[calc(100vh-12rem)] max-w-3xl overflow-auto">
          <table className="w-full text-sm tabular-nums">
            <thead className="sticky top-0 z-10 text-xs text-ink-soft [&_th]:bg-white">
              <tr className="border-b border-line">
                <th className="px-2 py-1.5 text-left">이름</th><th className="px-2 py-1.5 text-left">구분</th>
                <th className="px-2 py-1.5 text-right">영업계</th><th className="px-2 py-1.5 text-right">재무계</th><th className="px-2 py-1.5 text-right">차인지급액</th>
                <th className="px-2 py-1.5 text-left">정산 체크</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={`${r.axis}|${r.id}`} className="border-b border-line/60 hover:bg-surface-soft">
                  <td className="px-2 py-1">{r.name}</td>
                  <td className="px-2 py-1 text-ink-soft">{r.axis === 'owner' ? '차주' : r.driver_kind ?? '직영기사'}</td>
                  <td className="px-2 py-1 text-right">{money(r.sales_total)}</td>
                  <td className="px-2 py-1 text-right">{money(r.fin_total)}</td>
                  <td className="px-2 py-1 text-right font-medium text-red-700">{money(r.grand_total)}</td>
                  <td className="px-2 py-1 text-xs text-ink-soft">{r.is_salary ? '✓' : ''}</td>
                </tr>
              ))}
              {shown.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-ink-soft">마이너스인 사람이 없습니다.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
